import getContextIndexByChartType from "./strategies/contextIndexResolver";
import getTranslateStrategyByChartType from "./strategies/translateResolver";
import getEnunciadoStrategyByChartType from "./strategies/enunciadoResolver";
import { ChartManager } from "..";
import { LINE, TREE_MAP, GRAPH_BUBBLE } from "src/components/Charts/types";

/**
 * Lida com a aplicação do efeito de "highlit" no item clicado no gráfico
 * @param {ChartManager} context Instância do gerente do gráfico atual
 * @param {number} index Índice do item do gráfico clicado 
 */
function onClickedIndexChanged(context, index) {
  // Recupera as instâncias dos gerenciadores, por quê um array de gerenciadores?
  // O gráfico de cruzamentos simples pode ter mais de uma instância de gráficos,
  // portanto, é necessário que todas as instâncias apliquem as alterações em cada
  // um dos gráficos.
  context.getInstances(true).forEach((manager) => {
    manager.updateProps(null, null, null, index);
    manager.setUpdateOnly({ highlitIndex: true });
    manager._handleEchartsUpdates(true);
  });
}

/**
 * Gerente das funcionalidades do color picker que correspondem aos gráficos
 * possui três métodos principais:
 * - openColorPicker: Responsável pela lógica acionada no evento de duplo clique no item de um gráfico.
 * - hideColorPicker: Responsável pela lógica acionada quando o color picker é fechado.
 * - changeColorHandler: Responsável pela lógica de mudança da cor selecionada pelo usuário no gráfico.
 */
export default class ColorPickerManager {
  /**
   *
   * @param {ChartManager} manager Gerenciador do gráfico atual
   */
  constructor(manager) {
    this.manager = manager;
    this.identifier = this.manager.getIdentifier();
    this.chartInstance = this.manager.instance;
    this.clickedItemColorIndex = -1;
  }
  /**
   * Event listener disparado na ação de doubleclick no item de um gráfico.
   * @param {object} ctx Contexto do evento
   */
  openColorPicker(ctx) {
    // Primeiro é verificado se existe erro no serviço de configuração do gráfico, pois caso exista
    // não deverá ser possível abrir o color picker.
    if (this.chartInstance.configurationError) {
      this.chartInstance.onConfigurationError();

      return;
    }

    // Recupera as opções (echartOption) do gráfico pelo estado atual
    const options = this.manager.getOptions();

    let x;
    let width;
    // x -> coordenada x da posição da barra clicada em relação ao gráfico
    // width -> largura em pixels da barra clicada
    if (ctx.event.topTarget && ctx.event.topTarget.shape) {
      x = ctx.event.topTarget.shape.x;
      width = ctx.event.topTarget.shape.width;
    }

    // dimensões do gráfico
    const chartWidth = ctx.event.event.target.clientWidth;

    // Opções de translate aplicados ao color picker de acordo com o gráfico de referência
    const { translateX, translateY } = getTranslateStrategyByChartType(
      ctx,
      this.identifier,
      chartWidth,
      x,
      width
    );

    // Índice do item clicado do gráfico de acordo com o gráfico de referência. Passamos
    // `options` para que estratégias hierárquicas (ex: treemap) possam resolver o índice
    // a partir do treePathInfo + legend.data.
    const ctxIndex = getContextIndexByChartType(ctx, this.identifier, options);

    // Atualizamos o estado do índice do gráfico clicado
    this.clickedItemColorIndex = ctxIndex;

    // Para LINE, TREE_MAP e o graph_bubble da correlação a cor atual do item clicado
    // vem direto do contexto do evento (ctx.color) — nesses casos não há um array linear
    // `options.color` indexado pelo enunciado (ou existe com offset, como no graph_bubble,
    // em que a posição 0 é reservada para a pergunta principal). Nos demais gráficos
    // (incluindo o simple_bubble, onde cada pergunta é uma série), indexamos
    // `options.color[ctxIndex]`.
    const usesContextColor = (
      this.identifier.includes(LINE)
      || this.identifier.includes(TREE_MAP)
      || this.identifier.includes(GRAPH_BUBBLE)
    );
    const previousColor = usesContextColor
      ? ctx.color
      : options.color[ctxIndex];

    // Opções que serão enviadas para o color picker fazer o handle das alterações
    const colorPickerOptions = {
      // Indica que o color picker deve estar em estado visível
      colorPickerIsVisible: true,
      // Indica a cor atual selecionada pelo usuário ao abrir o color picker
      activeColor: previousColor,
      // Indica a posição X de translate que o color picker irá ser inicializado
      translateX,
      // Indica a posição Y de translate que o color picker irá ser inicializado
      translateY,
    };

    // Recupera o método que atualiza as opções de entrada do color picker
    const { setOptions: setColorPickerOptions } =
      this.chartInstance.colorPickerRef.current;
    // Seta as opções definidas anteriormente
    setColorPickerOptions(colorPickerOptions);
    onClickedIndexChanged(this.manager, this.clickedItemColorIndex);
  }

  /**
   * Método disparado ao selecionar uma cor no color picker
   * @param {string} newColor Cor selecionada pelo usuário no color picker
   */
  changeColorHandler(newColor) {
    const { clickedItemColorIndex } = this;

    const options = this.manager.getOptions();

    // Propriedades que serão atualizadas no color picker
    const colorPickerOptions = {
      activeColor: newColor,
    };

    // Recupera o estado atual de visualização do color picker e o método que atualiza as opções
    // do color picker.
    const { setOptions: setColorPickerOptions } =
      this.chartInstance.colorPickerRef.current;

    setColorPickerOptions(colorPickerOptions);

    // Para o gráfico de tipo LINHA, as suas series de dados no echartOption possui o formato
    // [ serieItem1, serieItem1, serieItem2, serieItem2, ..., serieItemN, serieItemN], portanto
    // o índice do item clicado onde a nova cor deve ser aplicada não é devolvido de forma correta
    // se considerássemos apenas o "clickedItemColorIndex", porquê como visto no modelo acima,
    // o array de series possue, para cada item de série, uma duplicação do mesmo, e o índice
    // real nesse caso, será representado pelo cálculo (index - index / 2)
    const consistentColorIndex = !this.identifier.includes(LINE)
      ? clickedItemColorIndex
      : clickedItemColorIndex - clickedItemColorIndex / 2;

    // Atualiza o array de cores do gráfico de referência com a cor nova
    options.color[consistentColorIndex] = newColor;

    // Roda o ciclo padrão de update único de cores. Para a maioria dos gráficos isso
    // equivale a `setOption({ color })` (mesma semântica do código anterior); para
    // identifiers que precisam reconstruir as séries (ex: treemap, que pinta nó a nó via
    // `itemStyle.color`), o resolver de updateOnly por identifier assume o controle.
    this.manager.getInstances(true).forEach((manager) => {
      manager.updateProps(null, options.color);
      manager.setUpdateOnly({ colors: true });
      manager._handleEchartsUpdates(true);
    });
  }

  /**
   * Método disparado ao ocultar o color picker.
   */
  hideColorPicker() {
    // as cores configuradas em updateChartColors aqui serão submetidas para a API
    // (ao fechar o colorpicker)
    const options = this.manager.getOptions();
    const { clickedItemColorIndex } = this;
    const { updateChartColors } = this.chartInstance.props;

    const enunciadoAlterado = getEnunciadoStrategyByChartType(
      this.identifier,
      options,
      clickedItemColorIndex
    );
    const { setOptions: setColorPickerOptions, activeColor: previousColor } =
      this.chartInstance.colorPickerRef.current;

    // Propriedades que serão atualizadas no color picker
    const colorPickerOptions = {
      colorPickerIsVisible: false,
    };

    // Atualiza o estado interno do color picker
    setColorPickerOptions(colorPickerOptions);
    // as cores configuradas em updateChartColors aqui serão submetidas para a API
    // (ao fechar o colorpicker)
    updateChartColors(enunciadoAlterado, previousColor);

    // reset
    this.clickedItemColorIndex = -1;

    // reset highlit
    onClickedIndexChanged(this.manager, this.clickedItemColorIndex);
  }
}
